/**
 * Progression report schema. PRD-003 DATA-11 + PR-21 + PR-22 + AI-10.
 *
 * Pro-only deliverable. Generated monthly from the meta-note corpus and
 * delivered tasks. Eric reviews before send (AI-06).
 */

import { z } from 'zod';
import {
  JSONB_SCHEMA_VERSIONS,
  isoTimestampSchema,
  uuidSchema,
  versionedSchema,
} from './common.js';
import { taskTypeSchema } from './enums.js';

/**
 * Standard progression report content (corpus >= 3 deliveries).
 */
export const progressionReportStandardContentSchema = z.object({
  kind: z.literal('standard'),
  through_line: z.object({
    callout: z.string(),
    detail_md: z.string(),
  }),
  blind_spot: z.object({
    callout: z.string(),
    detail_md: z.string(),
  }),
  recommended_focus: z.string(),
  task_type_coverage: z.array(
    z.object({
      type: taskTypeSchema,
      count: z.number().int().nonnegative(),
    }),
  ),
  task_types_not_yet_used: z.array(taskTypeSchema).default([]),
});
export type ProgressionReportStandardContent = z.infer<
  typeof progressionReportStandardContentSchema
>;

/**
 * First-month variant. PRD-003 PR-22 - corpus < 3 deliveries.
 */
export const progressionReportFirstMonthContentSchema = z.object({
  kind: z.literal('first_month'),
  what_was_set_up: z.string(),
  what_to_watch_as_corpus_builds: z.string(),
  task_type_coverage: z.array(
    z.object({
      type: taskTypeSchema,
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type ProgressionReportFirstMonthContent = z.infer<
  typeof progressionReportFirstMonthContentSchema
>;

export const progressionReportContentSchema = z.discriminatedUnion('kind', [
  progressionReportStandardContentSchema,
  progressionReportFirstMonthContentSchema,
]);
export type ProgressionReportContent = z.infer<typeof progressionReportContentSchema>;

export const versionedProgressionReportContentSchema = versionedSchema(
  progressionReportContentSchema,
  JSONB_SCHEMA_VERSIONS.progressionReportContent,
);
export type VersionedProgressionReportContent = z.infer<
  typeof versionedProgressionReportContentSchema
>;

export const progressionReportSchema = z.object({
  id: uuidSchema,
  subscriber_id: uuidSchema,
  period_start: isoTimestampSchema,
  period_end: isoTimestampSchema,
  content: progressionReportContentSchema,
  content_schema_version: z
    .number()
    .int()
    .min(1)
    .default(JSONB_SCHEMA_VERSIONS.progressionReportContent),
  generated_at: isoTimestampSchema,
  reviewed_by_eric_at: isoTimestampSchema.nullable(),
  delivered_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
});
export type ProgressionReport = z.infer<typeof progressionReportSchema>;
